import { parse, unparse } from 'papaparse';
import moment from 'moment-timezone';
import { getBusinessConf } from '../core/config';

const CANCELLED = 'CANCELLED';
const ERROR = 'ERROR';
const PAID = 'PAID';
const PAID_EXTERNALLY = 'PAID_EXTERNALLY';
const PENDING_AUTHENTICATION = 'PENDING_AUTHENTICATION';
const PENDING_MANUAL_PAYMENT = 'PENDING_MANUAL_PAYMENT';
const REFUNDED = 'REFUNDED';
const REFUSED = 'REFUSED';

// PSPs able to process a refund. Anything else has no refund path, so the action stays hidden.
const REFUNDABLE_PSP_NAMES = ['ADYEN', 'STRIPE'];

// Locale-aware currency formatting, falling back to a fixed-decimal string if Intl fails.
export const formatMoney = (value, locale, currency) => {
  const number = Number(value) || 0;
  try {
    return new Intl.NumberFormat(locale ? locale.replace('_', '-') : undefined, {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(number);
  } catch (e) {
    return number.toFixed(2);
  }
};

// Currency symbol for a code (EUR -> "€", USD -> "$"), falling back to the code itself if Intl
// can't resolve one. Used for the amount-input adornment (the code is kept for formatMoney).
export const getCurrencySymbol = (currency, locale) => {
  try {
    const parts = new Intl.NumberFormat(locale ? locale.replace('_', '-') : undefined, {
      style: 'currency',
      currency: currency || 'EUR',
    }).formatToParts(0);
    const symbol = parts.find(part => part.type === 'currency');
    return symbol ? symbol.value : currency || '';
  } catch (e) {
    return currency || '';
  }
};

// Decimal places a currency allows (EUR/USD -> 2, JPY -> 0, KWD -> 3), 2 if Intl fails.
export const getCurrencyDecimals = currency => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR' }).resolvedOptions()
      .maximumFractionDigits;
  } catch (e) {
    return 2;
  }
};

/**
 * @typedef {Object} InvoiceActions
 * @property {'cancelInvoice'} CANCEL_INVOICE - Action to **cancel** an invoice.
 * @property {'creditNote'} CREDIT_NOTE - Action to **download** a credit note.
 * @property {'externalPayment'} EXTERNAL_PAYMENT - Action that set the invoice status to `PAID` and set `isPaidExternally` to true.
 * @property {'invoice'} INVOICE - Action to **download** an invoice.
 * @property {'payNow'} PAY_NOW - Action to **manually** execute a payment related to an invoice.
 * @property {'pay'} PAY - Action to **pay** (set the invoice status to `PENDING`).
 * @property {'refund'} REFUND - Action to refund.
 * @property {'refundNote'} REFUND_NOTE - Action to **download** a refund note.
 * @property {'tripId'} TRIP_ID - Link to redirect to the trip details.
 */

/**
 * @type {InvoiceActions}
 */
export const invoiceActions = {
  CANCEL_INVOICE: 'cancelInvoice',
  CREDIT_NOTE: 'creditNote',
  EXTERNAL_PAYMENT: 'externalPayment',
  INVOICE: 'invoice',
  PAY_NOW: 'payNow',
  PAY: 'pay',
  REFUND: 'refund',
  REFUND_NOTE: 'refundNote',
  TRIP_ID: 'tripId',
};

const {
  CANCEL_INVOICE,
  CREDIT_NOTE,
  EXTERNAL_PAYMENT,
  INVOICE,
  PAY,
  PAY_NOW,
  REFUND,
  REFUND_NOTE,
  TRIP_ID,
} = invoiceActions;

/**
 * Invoice status type
 * @typedef {('CANCELLED' | 'ERROR' | 'MOP_MISSING' | 'PAID' | 'PAID_EXTERNALLY' | 'PENDING_AUTHENTICATION' | 'PENDING_EXTERNAL_PAYMENT' | 'PENDING_MANUAL_PAYMENT' | 'PENDING' | 'REFUNDED' | 'REFUSED')} InvoiceStatus

/**
 * Check if the currentStatus is allowed for the action
 * @param {InvoiceStatus} currentStatus
 * @param {Array<InvoiceStatus>} allowedStatus
 * @returns {boolean}
 */
const checkAllowedStatus = (currentStatus, allowedStatus) => allowedStatus.includes(currentStatus);

/**
 * Get the displayed actions for the invoice
 * @param {Object} invoice
 * @param {InvoiceStatus} invoice.invoiceStatus
 * @param {('cancelInvoice' | 'creditNote' | 'externalPayment' | 'invoice' | 'payNow' | 'pay' | 'refund' | 'tripId')} invoice.action
 * @param {boolean} [invoice.paymentInProgress]
 * @param {boolean} [invoice.refundInProgress]
 * @param {string} [invoice.invoiceId]
 * @param {number} [invoice.amount] - Invoice amount, used to hide the refund action on a zero amount
 * @param {string} [invoice.pspName] - PSP handling the invoice, used to gate the refund action
 * @param {boolean} [invoice.customCheck=true] - Have priority over the default check
 * @param {boolean} [invoice.isAdmin=false] - If true, the action is displayed for admin users only
 * @param {boolean} [invoice.isBusinessOwner=false] - If true, the action is displayed for business owners only
 */
export const getInvoiceDisplayedActions = ({
  action,
  amount,
  customCheck = true,
  invoiceId,
  invoiceStatus,
  pspName,
  paymentInProgress,
  refundInProgress,
  isAdmin = false,
  isBusinessOwner = false,
}) => {
  let isDisplayed = false;

  const actionInProgress = paymentInProgress || refundInProgress;

  switch (action) {
    case CANCEL_INVOICE:
      isDisplayed =
        !actionInProgress &&
        !checkAllowedStatus(invoiceStatus, [PAID, PAID_EXTERNALLY, CANCELLED, REFUNDED]) &&
        isAdmin;
      break;
    case CREDIT_NOTE:
      isDisplayed = invoiceId && checkAllowedStatus(invoiceStatus, [CANCELLED]);
      break;
    case EXTERNAL_PAYMENT:
      isDisplayed =
        !actionInProgress &&
        !checkAllowedStatus(invoiceStatus, [CANCELLED, PAID, PAID_EXTERNALLY, PENDING_AUTHENTICATION, REFUNDED]) &&
        isAdmin;
      break;
    case INVOICE:
      isDisplayed = invoiceId && !checkAllowedStatus(invoiceStatus, [CANCELLED]) && (isAdmin || isBusinessOwner);
      break;
    case PAY_NOW:
      isDisplayed = !actionInProgress && checkAllowedStatus(invoiceStatus, [PENDING_MANUAL_PAYMENT]);
      break;
    case PAY:
      isDisplayed =
        !actionInProgress && checkAllowedStatus(invoiceStatus, [ERROR, REFUSED]) && (isAdmin || isBusinessOwner);
      break;
    case REFUND:
      // Same gate as the cumulative invoices tab: the invoice must be paid, carry a non-zero
      // amount and sit on a PSP that supports refunds. Once refunded, or while a refund is
      // running, the refund note action takes over from this one.
      isDisplayed =
        invoiceId &&
        checkAllowedStatus(invoiceStatus, [PAID]) &&
        amount > 0 &&
        REFUNDABLE_PSP_NAMES.includes(pspName) &&
        isAdmin;
      break;
    case REFUND_NOTE:
      isDisplayed = invoiceId && checkAllowedStatus(invoiceStatus, [REFUNDED]) && (isAdmin || isBusinessOwner);
      break;
    case TRIP_ID:
      isDisplayed = Boolean(invoiceId);
      break;
    default:
      break;
  }

  return customCheck && isDisplayed;
};

/**
 * Returns an alternate status based on the provided flags.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.mainStatus - The main status of the invoice.
 * @param {boolean} params.refundInProgress - Indicates if a refund is in progress.
 * @param {boolean} params.paymentInProgress - Indicates if a payment is in progress.
 * @param {boolean} params.isPaidExternally - Indicates if the invoice is paid externally.
 * @returns {string} The alternate status based on the provided flags.
 */
export const getAlternateStatus = ({ mainStatus, refundInProgress, paymentInProgress, isPaidExternally }) => {
  if (refundInProgress) {
    return 'REFUND_IN_PROGRESS';
  }
  if (paymentInProgress) {
    return 'PAYMENT_IN_PROGRESS';
  }
  if (isPaidExternally) {
    return 'PAID_EXTERNALLY';
  }
  return mainStatus;
};

/**
 * Checks if unit invoices are enabled for the current fleet.
 *
 * This function retrieves the business configuration and the current fleet ID from local storage.
 * It then checks if the current fleet ID is included in the list of allowed fleets for unit invoices.
 *
 * @returns {boolean} - Returns true if unit invoices are enabled for the current fleet, otherwise false.
 */
export const getIsUnitInvoicesEnabled = () => {
  const conf = getBusinessConf();
  const fleetId = localStorage.getItem('fleetID');

  const allowedFleets = conf.config && conf.config.businessUnitInvoicesVisible;

  if (Array.isArray(allowedFleets) && allowedFleets.includes(fleetId)) return true;

  return false;
};

/**
 * Applies rounding to a number based on the specified rounding type.
 * @param {number} value - The number to round.
 * @param {('nearest' | 'up' | 'down')} roundingType - The type of rounding to apply.
 * @returns {number} - The rounded number.
 */
export const applyRounding = (value, roundingType) => {
  if (!roundingType) return value;

  const numValue = parseFloat(value);
  if (Number.isNaN(numValue)) return value;

  switch (roundingType) {
    case 'up':
      return Math.ceil(numValue);
    case 'down':
      return Math.floor(numValue);
    case 'nearest':
    default:
      return Math.round(numValue);
  }
};

/**
 * @typedef {Object} Column
 * @property {string} name - The name of the column.
 * @property {('date' | 'monetary' | 'percentage')} [format] - The format of the column (e.g., 'monetary', 'date', 'percentage').
 * @property {('nearest' | 'up' | 'down')} [rounding] - The rounding method for the column (e.g., 'round', 'floor', 'ceil').
 */

/**
 * Format a CSV row based on the provided columns and options.
 * @param {Object} row - The row data to format.
 * @param {Column[]} columns - The columns to format the row data.
 * @param {Object} options - Options for formatting.
 * @param {string} [options.locale='en-US'] - Locale for formatting.
 * @param {string} [options.currency='USD'] - Currency for formatting.
 * @param {string} [options.timezone='UTC'] - Timezone for formatting.
 * @returns {Object} - The formatted row.
 */
export const formatCSVRow = (row, columns, { locale = 'en-US', currency = 'USD', timezone = 'UTC' }) => {
  const taxNameMap = {};

  Object.keys(row).forEach(key => {
    const match = key.match(/tax(\d+)\.name/);
    if (match) {
      const index = match[1];
      const name = row[key];
      if (name) {
        taxNameMap[`tax${index}.amount`] = name;
      }
    }
    if (key === 'trip.duration') {
      taxNameMap[key] = `${key} (min)`;
    }
  });

  // Build the formatted row in the correct order, skipping taxX.name columns
  const formattedRow = {};
  columns.forEach(({ name, format, rounding }) => {
    // Skip taxX.name columns
    if (/tax\d+\.name/.test(name)) return;

    let value = row[name];

    // If this is a tax amount and has a mapped name, use it
    const finalKey = taxNameMap[name] || name;

    if (format === 'monetary') {
      value = (() => {
        const amount = parseFloat(value);

        if (!Number.isNaN(amount)) {
          let roundedAmount = amount;
          if (rounding) {
            roundedAmount = applyRounding(amount, rounding);
          }

          return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(roundedAmount);
        }
        return value;
      })();
    } else if (format === 'date') {
      value = (() => {
        try {
          if (!value) return value;
          const m = timezone ? moment.tz(value, timezone) : moment(value);
          if (!m.isValid()) return value;
          return m.format('YYYY-MM-DD HH:mm:ss');
        } catch {
          return value;
        }
      })();
    } else if (format === 'percentage') {
      value = (() => {
        const amount = parseFloat(value);
        if (!Number.isNaN(amount)) {
          let roundedAmount = amount;
          if (rounding) {
            roundedAmount = applyRounding(amount, rounding);
            return `${roundedAmount}%`;
          }
          return `${amount.toFixed(2)}%`;
        }
        return value;
      })();
    } else if (rounding) {
      const numValue = parseFloat(value);
      if (!Number.isNaN(numValue)) {
        value = applyRounding(numValue, rounding).toString();
      }
    }

    formattedRow[finalKey] = value;
  });

  return formattedRow;
};

/**
 * Format the CSV file
 * @param {string} rawData - The raw CSV data as a string.
 * @param {Object} options - Options for formatting.
 * @param {Object} [options.config] - Configuration object.
 * @param {Column[]} [options.config.columns] - Columns to format.
 * @param {string} [options.locale='en-US'] - Locale for formatting.
 * @param {string} [options.currency='USD'] - Currency for formatting.
 * @param {string} [options.timezone='UTC'] - Timezone for formatting.
 * @returns {Blob} - The formatted CSV file as a Blob.
 */
export const formatInvoicesCSVFile = (csvText, options) => {
  const { config, locale = 'en-US', currency = 'USD', timezone = 'UTC' } = options;

  let textToProcess = '';

  if (typeof csvText === 'string') {
    textToProcess = csvText;
  } else if (csvText && csvText.data && typeof csvText.data === 'string') {
    textToProcess = csvText.data;
  }

  const { data } = parse(textToProcess, {
    header: true,
    dynamicTyping: true,
    error: error => console.error('Error parsing CSV:', error),
  });
  const columns =
    (config && Array.isArray(config.columns) && config.columns) ||
    (Array.isArray(data) && data.length > 0 ? Object.keys(data[0]).map(name => ({ name })) : []);

  const formattedData = data.map(row => {
    let formattedRow = { ...row };
    if (Array.isArray(columns)) {
      formattedRow = formatCSVRow(row, columns, { locale, currency, timezone });
    }

    return formattedRow;
  });

  const csv = unparse(formattedData);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

  return blob;
};
